import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import {
  getCreatorDiscoverySearchStatus,
  getCurrentDiscoverySession,
  listCreatorDiscoverySearchResults,
  listDiscoveryMessages,
  type ListCreatorDiscoverySearchResultsRequest,
  type ListDiscoveryMessagesRequest,
  type SearchResultListResponse,
} from './api';
import { discoveryQueryKeys } from './query-keys';

export type DiscoverySearchResultsRequest = Omit<
  ListCreatorDiscoverySearchResultsRequest,
  'attempt'
> & {
  required_post_features?: string[];
};

export function currentDiscoverySessionQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: discoveryQueryKeys.currentSession(workspaceId),
    queryFn: ({ signal }) => getCurrentDiscoverySession(workspaceId, signal),
  });
}

export function discoveryMessagesInfiniteQueryOptions(
  workspaceId: string,
  discoverySessionId: string,
  request: Omit<ListDiscoveryMessagesRequest, 'cursor'> = {},
) {
  const queryRequest = request.limit == null ? undefined : request;

  return infiniteQueryOptions({
    queryKey: discoveryQueryKeys.messages(workspaceId, discoverySessionId, queryRequest),
    queryFn: ({ pageParam, signal }) =>
      listDiscoveryMessages(discoverySessionId, { ...request, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
  });
}

export function discoverySearchStatusQueryOptions(
  workspaceId: string,
  discoverySessionId: string,
  searchId: string,
) {
  return queryOptions({
    queryKey: discoveryQueryKeys.searchStatus(workspaceId, discoverySessionId, searchId),
    queryFn: ({ signal }) => getCreatorDiscoverySearchStatus(discoverySessionId, searchId, signal),
  });
}

export function discoverySearchResultsInfiniteQueryOptions(
  workspaceId: string,
  discoverySessionId: string,
  searchId: string,
  request: Pick<DiscoverySearchResultsRequest, 'show_saved'> = {},
  initialAttempt = 1,
) {
  return infiniteQueryOptions({
    queryKey: discoveryQueryKeys.searchResults(workspaceId, discoverySessionId, searchId, request),
    queryFn: ({ pageParam, signal }) =>
      listCreatorDiscoverySearchResults(
        discoverySessionId,
        searchId,
        { show_saved: request.show_saved, attempt: pageParam },
        signal,
      ),
    initialPageParam: initialAttempt,
    getNextPageParam: (lastPage, allPages) =>
      getNextDiscoverySearchResultsPageParam(lastPage, allPages, initialAttempt),
  });
}

export function getNextDiscoverySearchResultsPageParam(
  lastPage: SearchResultListResponse,
  allPages: SearchResultListResponse[],
  initialAttempt = 1,
) {
  const loadedCount = allPages.reduce((sum, page) => sum + page.creators.length, 0);
  const isTerminal = lastPage.complete === true && loadedCount >= lastPage.total_count;
  return isTerminal ? undefined : initialAttempt + allPages.length;
}
